import {
    Component,
    h,
    Element,
    Host,
    Prop,
    Watch,
    State,
    getAssetPath,
} from "@stencil/core";
import { debounce } from "../../utils/debounce";
import {
    addStylesheet,
    appendDataUid,
    setDateFields,
} from "./form-manipulation";

/**
 * Webinar component - the layout changes depending on the viewport
 * @slot header-text - The copy text in the header of the webinar
 * @slot info - details about the webinar (should be ux-iws-webinar-info)
 * @slot description - The description block of the webinar
 * @slot speaker - The speakers of the webinar (should be ux-iws-webinar-speaker elements)
 * @slot form - The registration form of the webinar
 */
@Component({
    tag: "ux-iws-webinar",
    styleUrl: "webinar.scss",
    assetsDirs: ["./assets"],
    shadow: true,
})
export class Webinar {
    @Element() domNode: HTMLUxIwsWebinarElement;

    /**
     * The title for the header
     */
    @Prop() headerTitle: string;

    /**
     * The background image for the header
     */
    @Prop() headerImage: string;

    /**
     * The title for the speakers section
     */
    @Prop() speakersTitle: string;

    /**
     * The first date for the date radio buttons
     */
    @Prop() date1: string;

    /**
     * The second date for the date radio buttons
     */
    @Prop() date2: string;

    /**
     * The date format for the date radio buttons
     */
    @Prop() dateFormat: string;

    /**
     * use the dark design for the header
     */
    @Prop({
        reflect: true,
    })
    dark: boolean = false;

    /**
     * Info that will be injected into the form as a hidden field
     */
    @Prop() webinarDataUid: string;

    @State() hasInfo: boolean = false;

    private descriptionRef: HTMLDivElement;
    private speakersRef: HTMLDivElement;
    private headerRef: HTMLDivElement;
    private formRef: HTMLDivElement;
    private speakers: string[];

    componentWillLoad() {
        this.hasInfo =
            this.domNode.querySelector("ux-iws-webinar-info") !== null;
        this.speakers = Array.from(
            this.domNode.querySelectorAll<HTMLUxIwsWebinarSpeakerElement>(
                "ux-iws-webinar-speaker[slot='speaker']"
            )
        ).map((el: HTMLUxIwsWebinarSpeakerElement) =>
            el.outerHTML.replace(/slot="speaker"/g, "")
        );
        setDateFields(this.domNode, this.dateFormat, this.date1, this.date2);
        appendDataUid(this.domNode, this.webinarDataUid);
        addStylesheet(getAssetPath("./assets/form-override.css"));
    }

    componentDidLoad() {
        this.resize();
        window.addEventListener("resize", () => this.resize());
        this.headerImageChanged();
    }

    disconnectedCallback() {
        window.removeEventListener("resize", () => this.resize());
    }

    @Watch("headerImage")
    headerImageChanged() {
        this.domNode.style.setProperty(
            "--ux-iws-webinar-header-image",
            `url(${this.headerImage})`
        );
    }

    /**
     * this calculation is needed to wrap the form next to description and speakers in the biggest breakpoint
     */
    private resize = debounce(() => {
        const descriptionHeight = this.descriptionRef.clientHeight;
        const speakersHeight = this.speakersRef.clientHeight;
        const formHeight = this.formRef.clientHeight;
        const totalHeight = descriptionHeight + speakersHeight + 2;
        this.domNode.style.setProperty(
            "--ux-iws-webinar-description-and-speakers-height",
            `${totalHeight}px`
        );
        const headerHeight = this.headerRef.clientHeight;
        this.domNode.style.setProperty(
            "--ux-iws-webinar-header-height",
            `${headerHeight}px`
        );
        const marginBottom = formHeight - headerHeight - totalHeight;
        this.domNode.style.setProperty(
            "--ux-iws-webinar-margin-bottom",
            `${marginBottom < 0 ? 0 : marginBottom}px`
        );
    }, 10);

    render() {
        return (
            <Host>
                <div class="header" ref={(el) => (this.headerRef = el)}>
                    <div class="header__image">
                        <h1 class="header__title headline headline--large">
                            {this.headerTitle}
                        </h1>
                        <div class="header__text">
                            <slot name="header-text"></slot>
                        </div>
                    </div>
                    {this.hasInfo && (
                        <div class="header__info">
                            <div class="header__info__content">
                                <slot name="info"></slot>
                            </div>
                        </div>
                    )}
                </div>
                <div class="flex-container">
                    <div
                        class="description"
                        ref={(el) => (this.descriptionRef = el)}
                    >
                        <slot name="description"></slot>
                    </div>
                    <div class="speakers" ref={(el) => (this.speakersRef = el)}>
                        {this.speakersTitle && (
                            <h2 class="speakers__title headline headline--small">
                                {this.speakersTitle}
                            </h2>
                        )}
                        <div class="speakers__mobile">
                            {this.speakers.length > 0 && (
                                <ux-slider no-arrows slides-auto-height>
                                    {this.speakers.map((speaker) => (
                                        <ux-slider-slide
                                            innerHTML={speaker}
                                        ></ux-slider-slide>
                                    ))}
                                    <ux-slider-pagination
                                        slot="pagination"
                                        bottom-pagination
                                    ></ux-slider-pagination>
                                </ux-slider>
                            )}
                        </div>
                        <div class="speakers__desktop">
                            <slot name="speaker"></slot>
                        </div>
                    </div>
                    <div class="form">
                        <div
                            class="form__shadow"
                            ref={(el) => (this.formRef = el)}
                        >
                            <slot name="form"></slot>
                        </div>
                    </div>
                </div>
            </Host>
        );
    }
}
